import { createSlice } from '@reduxjs/toolkit';
import { ColumnsMeta, ReportingPeriodDICTItem } from 'src/pages/SignInPage/_BLL/types';
import { apiRequest } from 'src/shared/api/api';
import { IdWithNameDTO } from 'src/shared/api/DTO';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import { sortByName } from 'src/shared/lib/array';
import { createAppAsyncThunk } from '../../../../app/redux/createAction';
import { dataPointToColumn } from '../../lib/dataPointToColumn/dataPointToColumn';
import { SelectedColumn } from '../customizeColumns/types';
import { CollectionItem, CollectionResItem, CollectionSaveItem } from './types';

const NAME = 'collection';

// * Parsing functions
const resToCollection = (res: CollectionResItem[], dataPointsMeta: ColumnsMeta['dataPoints'], reportingPeriodsDICT: ReportingPeriodDICTItem[]): CollectionItem[] => {
	return res.map((collectionItem) => {
		return {
			...collectionItem,
			columns: collectionItem.columns?.map((column) => dataPointToColumn(dataPointsMeta[column.datapointId], reportingPeriodsDICT, dataPointsMeta)) ?? [],
		};
	});
};

// * Async actions
const getCollectionList = createAppAsyncThunk(`${NAME}/getCollectionList`, async (_, thunkAPI) => {
	const res = await apiRequest.getRequest<IdWithNameDTO[]>({
		url: UrlAPI.savedColumnsList,
		thunkAPI,
	});
	return res;
});

// * Async actions
const getBasicCollectionInfo = createAppAsyncThunk(`${NAME}/getBasicCollectionInfo`, async (arg: { id: number }, thunkAPI) => {
	const { id } = arg;

	return await apiRequest.getRequest<CollectionResItem>({
		url: UrlAPI.savedColumnsInfo,
		params: { id },
		thunkAPI,
	});
});

const getCollectionInfo = createAppAsyncThunk(
	`${NAME}/getCollectionInfo`,
	async (arg: { id: number; dataPointsMeta: ColumnsMeta['dataPoints']; reportingPeriods: ReportingPeriodDICTItem[] }, thunkAPI) => {
		const { id, dataPointsMeta, reportingPeriods } = arg;

		const res = await apiRequest.getRequest<CollectionResItem>({
			url: UrlAPI.savedColumnsInfo,
			params: { id },
			thunkAPI,
		});

		const resWithTableHeader: CollectionItem = resToCollection([res], dataPointsMeta, reportingPeriods)[0];

		return resWithTableHeader;
	},
);

const addCollectionItem = createAppAsyncThunk(
	`${NAME}/addCollectionItem`,
	async (arg: { collection: CollectionSaveItem; dataPointsMeta: ColumnsMeta['dataPoints']; reportingPeriods: ReportingPeriodDICTItem[] }, thunkAPI) => {
		const { collection, dataPointsMeta, reportingPeriods } = arg;

		const payload: CollectionSaveItem = collection;

		const res = await apiRequest.postRequest<CollectionResItem[]>({
			url: UrlAPI.savedColumns,
			payload,
			thunkAPI,
		});

		return resToCollection(res, dataPointsMeta, reportingPeriods);
	},
);

const editCollectionItem = createAppAsyncThunk(
	`${NAME}/editCollectionItem`,
	async (arg: { payload: CollectionSaveItem; dataPointsMeta: ColumnsMeta['dataPoints']; reportingPeriods: ReportingPeriodDICTItem[] }, thunkAPI) => {
		const { payload, dataPointsMeta, reportingPeriods } = arg;

		const res: CollectionResItem[] = await apiRequest.postRequest<CollectionResItem[]>({
			url: UrlAPI.savedColumns,
			payload,
			thunkAPI,
		});

		return resToCollection(res, dataPointsMeta, reportingPeriods);
	},
);

const deleteCollectionItem = createAppAsyncThunk(
	`${NAME}/deleteColumns`,
	async (arg: { id: number; dataPointsMeta: ColumnsMeta['dataPoints']; reportingPeriods: ReportingPeriodDICTItem[] }, thunkAPI) => {
		const { id, dataPointsMeta, reportingPeriods } = arg;

		const res: CollectionResItem[] = await apiRequest.delRequest<CollectionResItem[]>({
			url: UrlAPI.savedColumns,
			params: { id },
			thunkAPI,
		});

		return resToCollection(res, dataPointsMeta, reportingPeriods);
	},
);

// * Reducer
interface State {
	collection: CollectionItem[] | null;
	status: RequestStatus;
}

export const initialState: State = {
	collection: null,
	status: RequestStatus.still,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(getCollectionList.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(getCollectionList.fulfilled, (state, action) => {
			const collection = action.payload;
			const copyOldCollectionCollectionItem: CollectionItem[] = state.collection ? [...state.collection] : [];
			state.collection = collection.map((item) => {
				const existingItem = copyOldCollectionCollectionItem.find((col) => typeof col.id === 'number' && typeof item.id === 'number' && col.id === item.id);
				return existingItem ? existingItem : { ...item, id: item.id ?? undefined, name: item.name ?? '', columns: [] as SelectedColumn[] };
			});
			state.status = RequestStatus.still;
		});
		builder.addCase(getCollectionList.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(getBasicCollectionInfo.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(getBasicCollectionInfo.fulfilled, (state, action) => {
			state.status = RequestStatus.still;
		});
		builder.addCase(getBasicCollectionInfo.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(getCollectionInfo.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(getCollectionInfo.fulfilled, (state, action) => {
			const collection = action.payload;
			if (state.collection === null) {
				state.collection = [collection]; // No need to sort if only one item
			} else {
				const existingIndex = state.collection.findIndex((item) => item.id === collection.id);
				if (existingIndex !== -1) {
					state.collection[existingIndex].columns = collection.columns;
				} else {
					state.collection.push(collection);
					state.collection = sortByName(state.collection) as CollectionItem[];
				}
			}
			state.status = RequestStatus.still;
		});
		builder.addCase(getCollectionInfo.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(addCollectionItem.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(addCollectionItem.fulfilled, (state, action) => {
			const collection = action.payload;

			state.collection = collection.length === 0 ? null : collection;
			state.status = RequestStatus.still;
		});
		builder.addCase(addCollectionItem.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(editCollectionItem.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(editCollectionItem.fulfilled, (state, action) => {
			const collection = action.payload;

			state.collection = collection.length === 0 ? null : collection;
			state.status = RequestStatus.still;
		});
		builder.addCase(editCollectionItem.rejected, (state) => {
			state.status = RequestStatus.failed;
		});

		builder.addCase(deleteCollectionItem.pending, (state) => {
			state.status = RequestStatus.loading;
		});
		builder.addCase(deleteCollectionItem.fulfilled, (state, action) => {
			const collection = action.payload;

			state.collection = collection.length === 0 ? null : collection;
			state.status = RequestStatus.still;
		});
		builder.addCase(deleteCollectionItem.rejected, (state) => {
			state.status = RequestStatus.failed;
		});
	},
});

export const actionsCollection = {
	...slice.actions,
	getCollectionList,
	getBasicCollectionInfo,
	getCollectionInfo,
	addCollectionItem,
	editCollectionItem,
	deleteCollectionItem,
};
